/**
 * Cart Context
 * Manages booking cart state with sessionStorage persistence.
 *
 * Epic: MVP 1.5.3 — Cart & Double-Entry Journal Ledger
 * Story: SCRUM-707 — CartContext and useCart Hook (Prerequisite)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'outdoorguides_cart';

const INITIAL_STATE = {
  cartId: null,
  items: [],
  status: 'idle',
  holdId: null,
  totals: {
    subtotalCents: 0,
  },
  guideId: null,
  experienceId: null,
};

function readCartFromStorage() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCartToStorage(cart) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  } catch {
    // sessionStorage may be full or unavailable — silently ignore
  }
}

function clearCartStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently ignore
  }
}

function recalculateTotals(items) {
  const subtotalCents = items.reduce((sum, item) => sum + (item.priceCents || 0), 0);
  return { subtotalCents };
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const stored = readCartFromStorage();
    if (!stored) {
      return INITIAL_STATE;
    }

    // Check hold expiry before hydrating
    if (stored.holdExpiresAt && new Date(stored.holdExpiresAt).getTime() < Date.now()) {
      clearCartStorage();
      return { ...INITIAL_STATE, status: 'expired' };
    }

    return {
      cartId: stored.cartId || null,
      items: Array.isArray(stored.items) ? stored.items : [],
      status: stored.status || 'idle',
      holdId: stored.holdId || null,
      totals: stored.totals || { subtotalCents: 0 },
      guideId: stored.guideId || null,
      experienceId: stored.experienceId || null,
    };
  });

  // Track holdExpiresAt separately so it persists to storage but isn't exposed in context value
  const holdExpiresAtRef = useRef(null);

  // On mount, read holdExpiresAt from storage
  useEffect(() => {
    const stored = readCartFromStorage();
    if (stored && stored.holdExpiresAt) {
      holdExpiresAtRef.current = stored.holdExpiresAt;
    }
  }, []);

  // Persist to sessionStorage on every state change (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (cart.status === 'expired' || cart.status === 'completed') {
      clearCartStorage();
      return;
    }

    if (cart.status === 'idle' && !cart.guideId && !cart.experienceId) {
      clearCartStorage();
      return;
    }

    writeCartToStorage({
      ...cart,
      holdExpiresAt: holdExpiresAtRef.current,
    });
  }, [cart]);

  const createCart = useCallback((guideId, experienceId) => {
    setCart((prev) => {
      if (prev.guideId && (prev.guideId !== guideId || prev.experienceId !== experienceId)) {
        holdExpiresAtRef.current = null;
        return {
          ...INITIAL_STATE,
          guideId,
          experienceId,
          status: 'active',
        };
      }

      if (prev.status === 'active' && prev.guideId === guideId && prev.experienceId === experienceId) {
        return prev;
      }

      return {
        ...INITIAL_STATE,
        guideId,
        experienceId,
        status: 'active',
      };
    });
  }, []);

  const addDate = useCallback((date, priceCents) => {
    setCart((prev) => {
      const exists = prev.items.some((item) => item.date === date);
      if (exists) {
        return prev;
      }
      const newItems = [...prev.items, { date, priceCents, bookingId: null }];
      return {
        ...prev,
        items: newItems,
        totals: recalculateTotals(newItems),
      };
    });
  }, []);

  const removeDate = useCallback((date) => {
    setCart((prev) => {
      const newItems = prev.items.filter((item) => item.date !== date);
      if (newItems.length === prev.items.length) {
        return prev;
      }
      return {
        ...prev,
        items: newItems,
        totals: recalculateTotals(newItems),
      };
    });
  }, []);

  const clearCart = useCallback(() => {
    holdExpiresAtRef.current = null;
    setCart(INITIAL_STATE);
    clearCartStorage();
  }, []);

  const getCart = useCallback(() => {
    return { ...cart };
  }, [cart]);

  const setHoldId = useCallback((holdId, holdExpiresAt) => {
    if (holdExpiresAt) {
      holdExpiresAtRef.current = holdExpiresAt;
    }
    setCart((prev) => ({ ...prev, holdId }));
  }, []);

  const setCartId = useCallback((cartId) => {
    setCart((prev) => ({ ...prev, cartId }));
  }, []);

  const markExpired = useCallback(() => {
    holdExpiresAtRef.current = null;
    setCart((prev) => ({ ...prev, status: 'expired' }));
    clearCartStorage();
  }, []);

  const markCompleted = useCallback(() => {
    holdExpiresAtRef.current = null;
    setCart((prev) => ({ ...prev, status: 'completed' }));
    clearCartStorage();
  }, []);

  const value = {
    cartId: cart.cartId,
    items: cart.items,
    status: cart.status,
    holdId: cart.holdId,
    totals: cart.totals,
    guideId: cart.guideId,
    experienceId: cart.experienceId,
    createCart,
    addDate,
    removeDate,
    clearCart,
    getCart,
    setHoldId,
    setCartId,
    markExpired,
    markCompleted,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * Hook to use cart context
 */
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
